import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import { ArrowLeft, Send, Save, Phone, Calendar, MapPin, Users, Clock, Pencil, X, Plane, Hash, TrendingUp, Percent, Wallet, Mail } from 'lucide-react';
import CaseJournal from '../components/CaseJournal';
import MandateStatus from '../components/MandateStatus';

const STATUS_FLOW = [
  'PENDING_REVIEW', 'ELIGIBLE', 'IN_PROGRESS', 'NEGOTIATING', 'WON', 'PAID'
];
const TERMINAL_STATUSES = ['INELIGIBLE', 'LOST'];

const STATUS_LABELS = {
  NEW: 'Nouveau', PENDING_REVIEW: 'En attente', ELIGIBLE: 'Éligible',
  IN_PROGRESS: 'En cours', NEGOTIATING: 'Négociation', WON: 'Gagné',
  PAID: 'Payé', LOST: 'Perdu', INELIGIBLE: 'Non éligible',
};

const STATUS_COLORS = {
  NEW: '#9CA3AF', PENDING_REVIEW: '#E8920A', ELIGIBLE: '#0D9F57',
  IN_PROGRESS: '#3B82F6', NEGOTIATING: '#8B5CF6', WON: '#0D9F57',
  PAID: '#0D1B3E', LOST: '#DC2626', INELIGIBLE: '#6B7280',
};

export default function CaseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [docRequest, setDocRequest] = useState('');
  const [sendingDoc, setSendingDoc] = useState(false);
  const [showDocInput, setShowDocInput] = useState(false);

  const load = useCallback(() => {
    api.caseById(id).then(data => {
      setC(data);
      setNotes(data.admin_notes || '');
      setEditForm({
        client_name: data.client_name || '',
        client_phone: data.client_phone || '',
        flight_number: data.flight_number || '',
        flight_date: data.flight_date ? data.flight_date.slice(0, 10) : '',
        departure_airport: data.departure_airport || '',
        arrival_airport: data.arrival_airport || '',
        airline: data.airline || '',
        delay_reason: data.delay_reason || '',
        delay_hours: data.delay_hours || '',
        passenger_count: data.passenger_count || 1,
        booking_reference: data.booking_reference || '',
      });
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (newStatus) => {
    if (!window.confirm(`Confirmer le passage au statut "${STATUS_LABELS[newStatus]}" ? Le client recevra une notification WhatsApp.`)) return;
    setSaving(true);
    try {
      await api.updateStatus(id, newStatus);
      await load();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.updateCase(id, { admin_notes: notes });
      await load();
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const setEditField = (field) => (e) => {
    setEditForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.updateCase(id, {
        ...editForm,
        passenger_count: parseInt(editForm.passenger_count) || 1,
        delay_hours: editForm.delay_hours ? parseFloat(editForm.delay_hours) : null,
        flight_date: editForm.flight_date || null,
      });
      setEditing(false);
      await load();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const requestDocument = async () => {
    if (!docRequest.trim()) return;
    setSendingDoc(true);
    try {
      await api.requestDocument(id, docRequest);
      setDocRequest('');
      setShowDocInput(false);
      await load();
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSendingDoc(false);
    }
  };

  const sendManualMessage = async () => {
    if (!message.trim()) return;
    setSendingMsg(true);
    try {
      await api.sendMessage(id, message);
      setMessage('');
      await load();
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSendingMsg(false);
    }
  };

  if (loading) return <p style={{ textAlign: 'center', padding: 60, color: '#888' }}>Chargement...</p>;
  if (!c) return <p style={{ textAlign: 'center', padding: 60, color: '#888' }}>Dossier introuvable</p>;

  const isTerminal = TERMINAL_STATUSES.includes(c.status);

  return (
    <div>
      <Link to="/cases" style={styles.back}><ArrowLeft size={16} /> Retour aux dossiers</Link>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dossier #{c.case_number}</h1>
          <p style={styles.subtitle}>Créé le {new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ ...styles.statusBadge, background: `${STATUS_COLORS[c.status]}15`, color: STATUS_COLORS[c.status] }}>
            {STATUS_LABELS[c.status] || c.status}
          </span>
          <button
            onClick={() => {
              const q = [c.client_name, c.flight_number, c.booking_reference].filter(Boolean).join(' ');
              navigator.clipboard?.writeText(q).catch(() => {});
              window.open(`https://mail.zoho.eu/zm/#mail/search/query/${encodeURIComponent(q)}`, '_blank');
            }}
            style={{ ...styles.zohoBtn, border: 'none', cursor: 'pointer' }}
            title="Ouvrir Zoho et copier le terme de recherche"
          >
            <Mail size={14} /> Mail
          </button>
          <button
            onClick={() => setEditing(e => !e)}
            style={styles.editToggleBtn}
            title={editing ? 'Annuler' : 'Modifier le dossier'}
          >
            {editing ? <X size={16} /> : <Pencil size={16} />}
          </button>
        </div>
      </div>

      <div style={styles.grid}>
        {/* LEFT COLUMN */}
        <div style={styles.col}>
          <Section title="Informations client">
            {editing ? (
              <>
                <EditField label="Téléphone WhatsApp" value={editForm.client_phone} onChange={setEditField('client_phone')} />
                <EditField label="Nom complet" value={editForm.client_name} onChange={setEditField('client_name')} />
                <EditField label="Référence (PNR)" value={editForm.booking_reference} onChange={setEditField('booking_reference')} />
              </>
            ) : (
              <>
                <InfoRow icon={Phone} label="Téléphone" value={c.client_phone} />
                <InfoRow icon={Users} label="Nom" value={c.client_name || 'Non renseigné'} />
                {c.booking_reference && (
                  <InfoRow icon={Hash} label="Référence (PNR)" value={c.booking_reference} bold />
                )}
              </>
            )}
          </Section>

          <Section title="Détails du vol">
            {editing ? (
              <>
                <EditField label="Numéro de vol" value={editForm.flight_number} onChange={setEditField('flight_number')} />
                <EditField label="Date du vol" type="date" value={editForm.flight_date} onChange={setEditField('flight_date')} />
                <EditFieldRow>
                  <EditField label="Aéroport départ" value={editForm.departure_airport} onChange={setEditField('departure_airport')} />
                  <EditField label="Aéroport arrivée" value={editForm.arrival_airport} onChange={setEditField('arrival_airport')} />
                </EditFieldRow>
                <EditField label="Compagnie aérienne" value={editForm.airline} onChange={setEditField('airline')} />
                <EditFieldRow>
                  <EditField label="Type de problème" value={editForm.delay_reason} onChange={setEditField('delay_reason')} />
                  <EditField label="Durée retard (h)" type="number" value={editForm.delay_hours} onChange={setEditField('delay_hours')} />
                </EditFieldRow>
                <EditField label="Nombre de passagers" type="number" value={editForm.passenger_count} onChange={setEditField('passenger_count')} />
                <button onClick={saveEdit} disabled={saving} style={styles.saveBtn}>
                  <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </>
            ) : (
              <>
                <InfoRow icon={Plane} label="Vol" value={c.flight_number || '—'} />
                <InfoRow icon={Calendar} label="Date" value={c.flight_date ? new Date(c.flight_date).toLocaleDateString('fr-FR') : '—'} />
                <InfoRow icon={MapPin} label="Trajet" value={`${c.departure_airport || '?'} → ${c.arrival_airport || '?'}`} />
                <InfoRow icon={Plane} label="Compagnie" value={c.airline || '—'} />
                <InfoRow icon={Clock} label="Problème" value={c.delay_reason || '—'} />
                <InfoRow icon={Users} label="Passagers" value={c.passenger_count || 1} />
              </>
            )}
          </Section>

          <Section title="Indemnisation">
            <InfoRow icon={TrendingUp} label="Montant total" value={c.total_compensation ? `${c.total_compensation}€` : '—'} bold />
            <InfoRow icon={Percent} label="Commission FlyCash (30%)" value={c.flycash_fee ? `${c.flycash_fee}€` : '—'} />
            <InfoRow icon={Wallet} label="Part client (70%)" value={c.client_amount ? `${c.client_amount}€` : '—'} bold />
          </Section>

          <MandateStatus caseId={id} clientPhone={c.client_phone} />

          <Section title="Notes rapides (admin)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note rapide privée (non horodatée)..."
              style={styles.textarea}
            />
            <button onClick={saveNotes} disabled={saving} style={styles.saveBtn}>
              <Save size={14} /> Enregistrer
            </button>
          </Section>
        </div>

        {/* RIGHT COLUMN */}
        <div style={styles.col}>
          <Section title="Changer le statut">
            <div style={styles.statusGrid}>
              {STATUS_FLOW.map((s) => (
                <button
                  key={s}
                  onClick={() => changeStatus(s)}
                  disabled={saving || c.status === s}
                  style={{
                    ...styles.statusBtn,
                    ...(c.status === s ? { background: STATUS_COLORS[s], color: '#fff', opacity: 1 } : {}),
                  }}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            <div style={styles.divider} />
            <div style={styles.statusGrid}>
              <button
                onClick={() => changeStatus('INELIGIBLE')}
                disabled={saving}
                style={{ ...styles.statusBtn, ...(c.status === 'INELIGIBLE' ? { background: STATUS_COLORS.INELIGIBLE, color: '#fff' } : { borderColor: '#DC2626', color: '#DC2626' }) }}
              >
                Non éligible
              </button>
              <button
                onClick={() => changeStatus('LOST')}
                disabled={saving}
                style={{ ...styles.statusBtn, ...(c.status === 'LOST' ? { background: STATUS_COLORS.LOST, color: '#fff' } : { borderColor: '#DC2626', color: '#DC2626' }) }}
              >
                Perdu
              </button>
            </div>
            {isTerminal && (
              <p style={styles.terminalNote}>Ce dossier est dans un état final.</p>
            )}
          </Section>

          <Section title="Envoyer un message WhatsApp">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Écrire un message au client... (si hors fenêtre 24h, envoyé via template automatiquement)"
              style={styles.textarea}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={sendManualMessage} disabled={sendingMsg || !message.trim()} style={{ ...styles.sendBtn, flex: 1 }}>
                <Send size={14} /> {sendingMsg ? 'Envoi...' : 'Envoyer'}
              </button>
              <button onClick={() => setShowDocInput(v => !v)} style={{ ...styles.sendBtn, background: '#E8920A', flex: 'none', padding: '10px 14px' }} title="Demander un document">
                📎 Doc
              </button>
            </div>
            {showDocInput && (
              <div style={{ marginTop: 10, padding: 12, background: '#FFF8F0', borderRadius: 8, border: '1px solid #F0C080' }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#E8920A' }}>Demander un document au client</p>
                <input
                  value={docRequest}
                  onChange={(e) => setDocRequest(e.target.value)}
                  placeholder="ex: numéro de billet (13 chiffres), carte d'embarquement..."
                  style={{ ...styles.editInput, width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
                />
                <button onClick={requestDocument} disabled={sendingDoc || !docRequest.trim()} style={{ ...styles.sendBtn, background: '#E8920A', marginTop: 0 }}>
                  <Send size={14} /> {sendingDoc ? 'Envoi...' : 'Envoyer la demande'}
                </button>
              </div>
            )}
          </Section>

          <Section title="Historique de conversation">
            <div style={styles.chatBox}>
              {(c.messages || []).length === 0 ? (
                <p style={styles.empty}>Aucun message</p>
              ) : (
                c.messages.map((m) => (
                  <div key={m.id} style={{
                    ...styles.chatBubble,
                    ...(m.direction === 'outbound' ? styles.chatBubbleOut : styles.chatBubbleIn)
                  }}>
                    <p style={styles.chatText}>{m.content}</p>
                    <span style={styles.chatTime}>{new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))
              )}
            </div>
          </Section>
        </div>
      </div>

      {/* JOURNAL — full width below the 2-col grid */}
      <div style={{ marginTop: 20 }}>
        <CaseJournal caseId={id} />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, bold }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{Icon && <Icon size={13} style={{ marginRight: 6, verticalAlign: -2 }} />}{label}</span>
      <span style={{ ...styles.infoValue, ...(bold ? { fontWeight: 800 } : {}) }}>{value}</span>
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text' }) {
  return (
    <div style={styles.editField}>
      <label style={styles.editLabel}>{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={onChange}
        style={styles.editInput}
      />
    </div>
  );
}

function EditFieldRow({ children }) {
  return <div style={styles.editFieldRow}>{children}</div>;
}

const styles = {
  back: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#666', textDecoration: 'none', fontSize: 14, marginBottom: 16, fontWeight: 600 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 800, color: '#0D1B3E', margin: 0 },
  subtitle: { color: '#888', fontSize: 13, margin: '4px 0 0' },
  statusBadge: { fontSize: 13, fontWeight: 700, padding: '8px 16px', borderRadius: 100 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  col: { display: 'flex', flexDirection: 'column', gap: 16 },
  section: { background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: 14, fontWeight: 800, color: '#0D1B3E', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: 0.5 },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  infoLabel: { color: '#888' },
  infoValue: { color: '#0D1B3E', fontWeight: 600 },
  textarea: { width: '100%', minHeight: 80, padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  saveBtn: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, background: '#0D1B3E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  zohoBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#fff', textDecoration: 'none', padding: '6px 12px', borderRadius: 8, background: '#0D1B3E' },
  editToggleBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: 8, width: 36, height: 36, cursor: 'pointer', color: '#0D1B3E' },
  editField: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 },
  editLabel: { fontSize: 12, fontWeight: 700, color: '#888' },
  editInput: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  editFieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  sendBtn: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, background: '#0D9F57', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  statusGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  statusBtn: { padding: '10px 8px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#666', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: 0.85 },
  divider: { height: 1, background: '#f0f0f0', margin: '12px 0' },
  terminalNote: { fontSize: 12, color: '#999', marginTop: 10, marginBottom: 0, fontStyle: 'italic' },
  chatBox: { maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  chatBubble: { maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 13 },
  chatBubbleIn: { background: '#F0EDE8', alignSelf: 'flex-start' },
  chatBubbleOut: { background: '#0D1B3E', color: '#fff', alignSelf: 'flex-end' },
  chatText: { margin: 0, whiteSpace: 'pre-wrap' },
  chatTime: { fontSize: 10, opacity: 0.6, display: 'block', marginTop: 4 },
  empty: { color: '#999', fontSize: 13, textAlign: 'center' },
};
